import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import Table from 'apollo-react/components/Table';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DetailRow from './components/DetailRow';
import ActionCell from './controls/ActionCell';
import ExpandCell from './controls/ExpandCell';
import SelectCell from './controls/SelectCell';
import TextFieldCell from './controls/TextFieldCell';
import TextViewCell from './controls/TextViewCell';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { equalityFn } from '../../../form-designer/utils/form-manager.utils';
import {
  deleteView,
  editView,
  setSelectedViewId,
} from '../../actions/report-model-designer.actions';
import {
  DEFAULT_ENTITY_NAME_WIDTH,
  ENTITY_ALIAS,
  ENTITY_NAME,
  ENTITY_NAME_WIDTH_OFFSET,
  FILTER,
  LINK_SOURCE,
  LINK_TARGET,
  OBJECT_KEY,
} from '../../constants/report-model-designer.constants';
import { getFieldLists, getRootView } from '../../selectors/report-model-designer.selectors';
import { findAllChildViews, findAllChildViewsLevel } from '../../utils/report-model-designer-utils';

const useStyles = makeStyles({
  tableContainer: {
    height: '100%',
    paddingLeft: 10,
    '& tr': {
      borderBottom: 'none !important',
    },
    '& div': {
      height: '100%',
    },
    '& tr div': {
      paddingRight: '0px !important',
    },
  },
  selected: {
    '& a, button, p': {
      color: 'white',
    },
    '& td': {
      backgroundColor: '#0768fd !important',
    },
  },
});

const columns = [
  {
    accessor: 'expand',
    customCell: ExpandCell,
    width: 50,
  },
  {
    header: 'Entity Name',
    accessor: ENTITY_NAME,
    customCell: TextViewCell,
    width: 200,
  },
  {
    header: 'Unique Alias',
    accessor: ENTITY_ALIAS,
    customCell: TextFieldCell,
    width: 400,
  },
  {
    header: 'Identifier',
    accessor: OBJECT_KEY,
    customCell: SelectCell,
    width: 200,
  },
  {
    header: 'Link Source',
    accessor: LINK_SOURCE,
    customCell: SelectCell,
    width: 200,
  },
  {
    header: 'Link Target',
    accessor: LINK_TARGET,
    customCell: SelectCell,
    width: 200,
  },
  {
    header: 'Filter',
    accessor: FILTER,
    customCell: TextFieldCell,
    width: 600,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    width: 55,
  },
];

const ReportModel = ({ views, selectedViewId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const fieldLists = useSelector(getFieldLists, equalityFn);
  const rootView = useSelector(getRootView, equalityFn);

  const [currentExpandedRows, setCurrentExpandedRows] = useState([rootView?.Id]);

  const handleToggleRow = useCallback(
    (currentId) => {
      const childViewsIds = [];

      findAllChildViews(views, currentId, childViewsIds);

      if (!childViewsIds.includes(selectedViewId)) {
        setCurrentExpandedRows((currentExpandedRows) => {
          if (currentExpandedRows.includes(currentId)) {
            return currentExpandedRows.filter((row) => row !== currentId);
          } else {
            return [...currentExpandedRows, currentId];
          }
        });
      }
    },
    [selectedViewId, views]
  );

  const selectRow = useCallback(
    (id) => {
      dispatch(setSelectedViewId(selectedViewId === id ? null : id));

      if (currentExpandedRows.indexOf(id) === -1 && selectedViewId !== id) {
        setCurrentExpandedRows([...currentExpandedRows, id]);
      }
    },
    [currentExpandedRows, dispatch, selectedViewId]
  );

  const editRow = useCallback(
    (params) => {
      dispatch(editView(params));
    },
    [dispatch]
  );

  const deleteRow = useCallback(
    (id) => {
      dispatch(deleteView(id));
    },
    [dispatch]
  );

  const entityNameWidth = useMemo(() => {
    const childViewsLevels = [];
    findAllChildViewsLevel(
      views.filter((v) => currentExpandedRows.includes(v.Id)),
      rootView?.Id,
      childViewsLevels,
      1
    );

    const maxExpandedLevel = childViewsLevels.length > 0 ? Math.max(...childViewsLevels) : 0;

    const width =
      DEFAULT_ENTITY_NAME_WIDTH - maxExpandedLevel * ENTITY_NAME_WIDTH_OFFSET < 128
        ? DEFAULT_ENTITY_NAME_WIDTH + maxExpandedLevel * ENTITY_NAME_WIDTH_OFFSET - 128
        : DEFAULT_ENTITY_NAME_WIDTH;

    return width;
  }, [currentExpandedRows, rootView?.Id, views]);

  const rows = useMemo(
    () =>
      rootView
        ? [
            {
              ...rootView,
              EntityName: rootView.TranslatedName || rootView.Name,
              expanded: currentExpandedRows.includes(rootView.Id) && rootView.HasChildren,
              views,
              currentExpandedRows,
              fieldLists,
              selectedViewId,
              className: classNames(selectedViewId === rootView.Id && classes.selected),
              level: 1,
              entityNameWidth,
              handleToggleRow,
              selectRow,
              editRow,
              deleteRow,
            },
          ]
        : [],
    [
      classes.selected,
      currentExpandedRows,
      deleteRow,
      editRow,
      entityNameWidth,
      fieldLists,
      handleToggleRow,
      rootView,
      selectRow,
      selectedViewId,
      views,
    ]
  );

  return (
    <Grid style={{ overflowX: 'auto', width: '100%' }} item xs={12}>
      <div className={classes.tableContainer}>
        <Table
          columns={columns}
          rows={rows}
          emptyProps={{ text: translateString('No data to display') }}
          rowId="Id"
          hidePagination
          ExpandableComponent={DetailRow}
        />
      </div>
    </Grid>
  );
};

export default ReportModel;
